import { useCallback, useEffect, useRef, useState } from 'react';
import {
  thoughtsFor, ideaRows, canStart, pickSession, readMs, QUIET_LINE,
  loadMemory, saveMemory, noteSession, noteShown, noteDone, noteClosed,
} from '../../agent/bailey/thoughts';
import { placeFor, placeForPath } from '../../agent/bailey/skills/knowledge';
import { loadBrandKit } from '../../marketing-v2/features/email-composer/email/theme';
import { loadPosts } from '../../marketing-v2/features/social/data/store';

/**
 * The desk pet's thinking out loud (thoughts.js has the what and when).
 *
 * Every few seconds, while Bailey is left alone and nothing of his is open,
 * this checks whether a session may start. If so it gathers the facts (the
 * brand kit, Social's drafts, and the diary's free times on pages where they
 * matter) and shows up to three thoughts, one at a time. Nothing starts
 * while someone is typing, while a panel or modal covers the page, or while
 * the tab is hidden, and a session gives way the moment Bailey is needed.
 */

const DAY = 24 * 60 * 60 * 1000;
const CHECK_MS = 3000;
const CLOUD_MS = 12 * 1000; // on a phone: how long the small cloud waits for a tap
const NOTHING_WAIT = 60 * 1000; // after "nothing worth saying", before looking again

const read = (k) => { try { return localStorage.getItem(k) || ''; } catch (e) { return ''; } };

// Free times from the diary, read at most every five minutes and shared
let freeRead = null;
let freeValue = { salon: '', value: null };
const readFree = () => {
  const salon = read('salonId');
  if (freeRead && freeRead.salon === salon && Date.now() - freeRead.at < 5 * 60 * 1000) return freeRead.job;
  const job = import(/* webpackChunkName: "social-free-slots" */ '../../marketing-v2/features/social/data/freeSlots')
    .then((m) => m.findFreeSlots({ days: 3 }))
    .then((r) => (r ? { date: r.date, dayLabel: r.dayLabel, count: r.count } : null))
    .catch(() => null)
    .then((value) => { freeValue = { salon, value }; return value; });
  freeRead = { salon, at: Date.now(), job };
  return job;
};
const knownFree = () => (freeValue.salon === read('salonId') ? freeValue.value : null);
const needsFree = (path) => /^\/(diary|marketing)/.test(path || '') && !!placeFor('social');

// What thoughts.js needs to know, from what this browser already has
const factsFor = (path, now, free) => {
  const can = { social: !!placeFor('social'), brandKit: !!placeFor('brandKit'), finance: !!placeFor('finance') };
  let kit = {};
  try { kit = loadBrandKit(read('salonId')) || {}; } catch (e) { /* no kit */ }
  const posts = can.social ? loadPosts() : [];
  const until = now.getTime() + 7 * DAY;
  return {
    path,
    now,
    can,
    brand: { set: Object.keys(kit).length > 0, logo: !!kit.logo },
    drafts: posts.filter((p) => p.status === 'draft').length,
    planned: posts.filter((p) => p.status === 'planned' && p.plannedAt && new Date(p.plannedAt) >= now && new Date(p.plannedAt).getTime() < until).length,
    free,
  };
};

// Someone typing, or a panel or modal over the page (the shared scroll lock
// fixes the body while one is open)
const typing = (inside) => {
  const el = document.activeElement;
  if (!el || (inside && inside.contains(el))) return false;
  return !!(el.matches && el.matches('input, textarea, select, [contenteditable=""], [contenteditable="true"]'));
};
const covered = () => {
  try {
    return document.body.style.position === 'fixed' || !!document.querySelector('.sp-overlay, [aria-modal="true"]');
  } catch (e) { return false; }
};

/**
 * `enabled`: the pet is on screen with a chat to offer. `ready`: nothing of
 * his is open and he isn't busy or being carried. `calm`: he isn't in the
 * middle of an antic (a session waits for it to finish). `dock`: his element,
 * so typing in his own ask box doesn't count. `compact`: a phone, where only
 * a small cloud shows (the ideas are in the ask box it opens), so the ideas
 * don't count as seen.
 */
export default function useThoughts({ enabled, ready, calm, path, dock, compact = false }) {
  const userId = read('userId');
  const [session, setSession] = useState(null); // { list, index }
  const [quiet, setQuiet] = useState(false); // his one line after three closes
  // Read fresh every time: another tab may have closed an idea, or quietened
  // him for the day, since this one last looked
  const mem = useCallback(() => loadMemory(userId), [userId]);
  const keep = useCallback((next) => { saveMemory(userId, next); }, [userId]);
  const pageSince = useRef(Date.now());
  const lastNothing = useRef(0);
  const checking = useRef(false);
  const live = useRef({ ready, calm, path, compact });
  live.current = { ready, calm, path, compact };

  useEffect(() => { pageSince.current = Date.now(); }, [path]);

  const current = session ? session.list[session.index] : null;
  const end = useCallback(() => setSession(null), []);

  // Looking for a moment to think out loud
  useEffect(() => {
    if (!enabled || !ready || session) return undefined;
    const check = async () => {
      if (checking.current) return;
      const { calm: isCalm, path: here } = live.current;
      const now = Date.now();
      if (!isCalm || document.hidden || now - lastNothing.current < NOTHING_WAIT) return;
      if (typing(dock && dock.current) || covered()) return;
      if (!canStart(mem(), { now, path: here, pageSince: pageSince.current })) return;
      checking.current = true;
      try {
        const free = needsFree(here) ? await readFree() : null;
        // Still a good moment, on the same page, once the diary has answered?
        const after = live.current;
        if (!after.ready || !after.calm || after.path !== here || typing(dock && dock.current) || covered()) return;
        const list = pickSession(thoughtsFor(factsFor(here, new Date(), free)), mem(), Date.now());
        if (!list.length) { lastNothing.current = Date.now(); return; }
        const started = noteSession(mem(), { now: Date.now(), path: here });
        keep(live.current.compact ? started : noteShown(started, list[0].id));
        setSession({ list, index: 0 });
      } finally {
        checking.current = false;
      }
    };
    const t = setInterval(check, CHECK_MS);
    return () => clearInterval(t);
  }, [enabled, ready, session, dock, keep, mem]);

  // Bailey is needed (his chat, a reply, being carried): the session gives way
  useEffect(() => { if (session && (!enabled || !ready)) setSession(null); }, [enabled, ready, session]);

  // Each thought stays up long enough to read, and waits while it's held
  const [held, setHeld] = useState(false);
  // A bubble that goes while it's held (tapped, closed) mustn't hold the next one
  useEffect(() => { if (!session) setHeld(false); }, [session]);
  const left = useRef(0);
  const shownAt = useRef(0);
  useEffect(() => {
    if (!current) return;
    left.current = compact ? CLOUD_MS : readMs(current.said);
  }, [current]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!current || held) return undefined;
    shownAt.current = Date.now();
    const t = setTimeout(() => {
      setSession((s) => {
        if (!s) return s;
        // The phone's cloud is one wait; the bubble goes idea by idea
        if (compact || s.index + 1 >= s.list.length) return null;
        keep(noteShown(mem(), s.list[s.index + 1].id));
        return { ...s, index: s.index + 1 };
      });
    }, left.current);
    return () => {
      clearTimeout(t);
      left.current = Math.max(0, left.current - (Date.now() - shownAt.current));
    };
  }, [current, held]); // eslint-disable-line react-hooks/exhaustive-deps

  const hold = useCallback((on) => setHeld(!!on), []);

  /** Tapped: it counts as done, and the pet acts on it. */
  const accept = useCallback(() => {
    if (!current) return null;
    keep(noteDone(mem(), current));
    setSession(null);
    return current;
  }, [current, keep, mem]);

  /** Closed: that idea sleeps, and three in a row quietens him for the day. */
  const hushTimer = useRef(null);
  useEffect(() => () => clearTimeout(hushTimer.current), []);
  const dismiss = useCallback(() => {
    if (!current) return;
    const { memory: next, quiet: hush } = noteClosed(mem(), current);
    keep(next);
    setSession(null);
    if (hush) {
      setQuiet(true);
      clearTimeout(hushTimer.current);
      hushTimer.current = setTimeout(() => setQuiet(false), 3200);
    }
  }, [current, keep, mem]);

  return {
    thought: current,
    index: session ? session.index : 0,
    total: session ? session.list.length : 0,
    held,
    hold,
    accept,
    dismiss,
    end,
    quietLine: quiet ? QUIET_LINE : null,
  };
}

/**
 * The ask box's idea rows for the page: the best thoughts in the person's
 * words, then the page's own questions. Filled in again once the diary's
 * free times arrive.
 */
export function useIdeaRows(open, path) {
  const [rows, setRows] = useState([]);
  useEffect(() => {
    if (!open) return undefined;
    let alive = true;
    const here = placeForPath(path);
    const asks = ((here && here.asks) || []).slice(0, 3);
    const fallback = here ? `What can you do on ${here.name}?` : 'What can you do for me?';
    const make = (free) => ideaRows(thoughtsFor(factsFor(path, new Date(), free)), asks, fallback, loadMemory(read('userId')));
    setRows(make(knownFree()));
    if (needsFree(path)) readFree().then((free) => { if (alive) setRows(make(free)); });
    return () => { alive = false; };
  }, [open, path]);
  return rows;
}
